import React, { useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import { parseTextWithURLs } from '../utils/urlDetector';

/**
 * InlineURLText component - Render text với inline clickable URLs
 * Thay thế URLs trong text bằng clickable links
 */
const InlineURLText = ({ text, className = "", isInput = false }) => {
    if (!text) return null;

    const segments = parseTextWithURLs(text);

    return (
        <span className={className}>
            {segments.map((segment, index) => {
                if (segment.type === 'url') {
                    return (
                        <a
                            key={index}
                            href={segment.content}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`text-blue-600 hover:text-blue-800 underline break-all ${
                                isInput ? 'cursor-pointer' : ''
                            }`}
                            onClick={(e) => {
                                if (isInput) {
                                    e.preventDefault();
                                }
                            }}
                            title={`${segment.urlType === 'file' ? 'File' : 'Website'}: ${segment.content}`}
                        >
                            {segment.content}
                        </a>
                    );
                } else {
                    return (
                        <span key={index}>
                            {segment.content}
                        </span>
                    );
                }
            })}
        </span>
    );
};

/**
 * InputWithInlineURLs - Uncontrolled input component for best performance
 * Uses ref-based approach to avoid re-renders on every keystroke
 */
export const InputWithInlineURLs = forwardRef(({
    defaultValue = '',
    onInput,
    onImagePaste,
    placeholder,
    className = "",
    onKeyDown,
    onFocus,
    onBlur,
    disabled,
    rows,
    ...props
}, ref) => {
    const internalRef = useRef(null);
    const pastedRef = useRef(false);

    useImperativeHandle(ref, () => ({
        get value() {
            return internalRef.current?.value || '';
        },
        set value(val) {
            if (internalRef.current) {
                internalRef.current.value = val;
            }
        },
        focus() {
            internalRef.current?.focus();
        },
        blur() {
            internalRef.current?.blur();
        },
        clear() {
            if (internalRef.current) {
                internalRef.current.value = '';
                internalRef.current.style.height = 'auto';
            }
        },
        get element() {
            return internalRef.current;
        }
    }), []);

    const handleInput = useCallback((e) => {
        const target = e.target;
        const wasPasted = pastedRef.current;
        pastedRef.current = false;

        requestAnimationFrame(() => {
            if (!target) return;

            const scrollParent = target.parentElement;
            const savedScrollTop = scrollParent?.scrollTop ?? 0;
            const wasAtBottom = scrollParent
                ? savedScrollTop >= scrollParent.scrollHeight - scrollParent.clientHeight - 5
                : false;

            target.style.height = 'auto';
            target.style.height = target.scrollHeight + 'px';

            if (scrollParent) {
                if (wasPasted || wasAtBottom) {
                    scrollParent.scrollTop = scrollParent.scrollHeight;
                } else {
                    scrollParent.scrollTop = savedScrollTop;
                }
            }
        });

        if (onInput) {
            onInput(e);
        }
    }, [onInput]);

    const handleKeyDown = useCallback((e) => {
        if (onKeyDown) {
            onKeyDown(e);
        }
    }, [onKeyDown]);

    const handlePaste = useCallback(async (e) => {
        const clipboardItems = e.clipboardData?.items;

        if (clipboardItems) {
            for (let i = 0; i < clipboardItems.length; i++) {
                const item = clipboardItems[i];

                if (item.type.startsWith('image/')) {
                    e.preventDefault();
                    const file = item.getAsFile();
                    if (file && onImagePaste) {
                        try {
                            await onImagePaste(file);
                        } catch (error) {
                            console.error('Error handling image paste:', error);
                        }
                    }
                    return;
                }
            }
        }

        pastedRef.current = true;
    }, [onImagePaste]);

    return (
        <textarea
            ref={internalRef}
            className={`${className} disabled:bg-transparent`}
            defaultValue={defaultValue}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onFocus={onFocus}
            onBlur={onBlur}
            placeholder={placeholder}
            disabled={disabled}
            rows={rows}
            {...props}
        />
    );
});

InputWithInlineURLs.displayName = 'InputWithInlineURLs';

export default InlineURLText;
